import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { isDialablePhone } from '../lib/telephony';
import { staffSleutel } from '../lib/staff-sleutel';

export interface Colleague {
  id: string;
  name: string;
  phone: string;
  team: string | null;
  /** SF Medewerker__c-Id; leeg voor iedereen die na de cut in dienst kwam. */
  sfMedewerkerId: string | null;
  /** Join-sleutel met route.chauffeurId: SF-id waar dat bestaat, anders staff.id. */
  sleutel: string;
}

interface DirectoryRow {
  id: string;
  name: string;
  phone: string | null;
  team: string | null;
  sf_medewerker_id: string | null;
}

// Sessie-cache: de belgids verandert zelden, dus we halen 'm één keer op en
// delen 'm tussen de dock-snelkies en de route-planner-belknop.
let cache: Colleague[] | null = null;
let inflight: Promise<Colleague[]> | null = null;

async function fetchDirectory(): Promise<Colleague[]> {
  if (cache) return cache;
  if (inflight) return inflight;

  inflight = (async () => {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (!token) return [];

    const res = await fetch('/api/workforce/staff?mode=directory', {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      inflight = null;
      throw new Error(`directory ${res.status}`);
    }
    const rows = (await res.json()) as DirectoryRow[];
    const list: Colleague[] = rows
      .filter((r) => isDialablePhone(r.phone))
      .map((r) => ({
        id: r.id,
        name: r.name,
        phone: r.phone as string,
        team: r.team,
        sfMedewerkerId: r.sf_medewerker_id,
        sleutel: staffSleutel(r),
      }));
    cache = list;
    inflight = null;
    return list;
  })();

  return inflight;
}

export interface UseColleagues {
  colleagues: Colleague[];
  /** Map op SF Medewerker__c-Id → collega (voor route.chauffeurId-lookup). */
  byMedewerkerId: Map<string, Colleague>;
  isLoading: boolean;
}

/**
 * Actieve collega's met een belbaar nummer, voor klik-om-te-bellen. Leunt op
 * de bestaande softphone-dial via <CallButton> / de dialpad — belt nooit stil.
 */
export function useColleagues(): UseColleagues {
  const [colleagues, setColleagues] = useState<Colleague[]>(cache ?? []);
  const [isLoading, setIsLoading] = useState(cache === null);

  useEffect(() => {
    if (cache) {
      setColleagues(cache);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    fetchDirectory()
      .then((list) => {
        if (!cancelled) setColleagues(list);
      })
      .catch((err) => console.error('[useColleagues] load failed:', err))
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const byMedewerkerId = useMemo(() => {
    const map = new Map<string, Colleague>();
    for (const c of colleagues) {
      map.set(c.sleutel, c);
    }
    return map;
  }, [colleagues]);

  return { colleagues, byMedewerkerId, isLoading };
}
